import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { AppConfig } from '../../shared/config/app-config';
import { LocalStorageService } from './local-storage.service';
import { KycPreviewData } from '../../shared/models/kyc.model';
import { BehaviorSubject, delay, mergeMap, Observable, of, retryWhen, Subject, throwError } from 'rxjs';
import {
    BusinessKycInformation,
    KYC_DOCUMENT_TYPE,
    KYCBusinessOwnerInformation,
    KYCBusinessOwnerUpgradeInformation,
    KycBusinessType,
    KYCFormKeys,
    KYCFormPhase,
    KYCFormRoute,
    KycNextOfKin,
    KycPersonalInformation,
    KycProcessKeys,
    KycProofOfAddress,
    kycRegisteredBusinessInformation,
    KycRegisteredBusinessRoutes,
    KYCRegisteredRequestData,
    KYCRequestData,
    KycRoute,
    kycUnregisteredBusinessInformation,
    KycUnregisteredBusinessRoutes,
    KycUnregisteredBusinessUpgradeInformation,
    MetaMapMetaData,
    MetaMapRefVerificationStatus,
    PrimerCard,
    RoutePath,
} from '../models/kyc.model';
import { Router } from '@angular/router';
import { GeneralService } from './general.service';
import { StringHelper } from '../helpers/strings.helper';
import { catchError, take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class KycService {
    static BASE_KYC_URL = '/kyc';
    proofAddressObserver = new Subject<any>();
    proofAddressObserver$ = this.proofAddressObserver.asObservable();
    selectedBusinessType = new Subject<any>();
    selectedBusinessType$ = this.selectedBusinessType.asObservable();
    setBusinessDetailsStatus = new Subject<any>();
    setBusinessDetailsStatus$ = this.setBusinessDetailsStatus.asObservable();
    metaMapKycWaitingDetails = new Subject<any>();
    metaMapKycWaitingDetails$ = this.metaMapKycWaitingDetails.asObservable();
    readonly DEFAULT_BUSINESS_OWNER_KYC_DATA: KYCRequestData = {
        personalInformation: null,
        proofOfAddress: null,
        nextOfKinInformation: null,
    };
    readonly MAX_MOBILE_WIDTH = 768;
    readonly PERCENTAGE_MULTIPLIER = 100;
    isMobile: boolean;
    private completedKyc: any;
    private data = new BehaviorSubject(Boolean);
    data$ = this.data.asObservable();
    private _step = new Subject<number>();
    _step$ = this._step.asObservable();
    private kycNavigationObserver = new Subject<any>();
    kycNavigationObserver$ = this.kycNavigationObserver.asObservable();
    private kycBusinessInformationObserver = new Subject<any>();
    kycBusinessInformationObserver$ = this.kycBusinessInformationObserver.asObservable();

    constructor(
        private apiClientHelper: ApiClientHelper,
        public config: AppConfig,
        private localStorage: LocalStorageService,
        private router: Router,
        private generalService: GeneralService,
    ) {}

    private static getNextOfKin(nextOfKin) {
        const kycNextOfKin: KycNextOfKin = {
            houseNumber: nextOfKin?.nextOfKinHouseNumber,
            lastName: nextOfKin?.nextOfKinLastName,
            firstName: nextOfKin?.nextOfKinFirstName,
            phoneNumber: nextOfKin?.nextOfKinPhoneNumber,
            relationship: nextOfKin?.relationship,
            dateOfBirth: nextOfKin?.nextOfKinBirthDate,
            houseAddress: nextOfKin?.houseAddress,
            lgaId: nextOfKin?.nextOfKinLocalGovernment?.id,
            lgaOfOrigin: nextOfKin?.nextOfKinLgaOfOrigin?.id,
            nationalityId: nextOfKin?.nextOfKinNationality?.id,
            stateId: nextOfKin?.nextOfKinLocalGovernment?.state?.id,
            stateOfOrigin: nextOfKin?.nextOfKinLgaOfOrigin?.state?.id,
            streetName: nextOfKin?.nextOfKinStreetName,
            key: KYCFormKeys.NEXT_OF_KIN,
        };
        return kycNextOfKin;
    }

    private static getPersonalInformation(personalInformation) {
        const kycPersonalInformation: KycPersonalInformation = {
            religion: personalInformation?.religion,
            employmentStatus: personalInformation?.employmentStatus,
            maritalStatus: personalInformation?.maritalStatus,
            houseAddress: personalInformation?.houseAddress,
            lgaId: personalInformation?.localGovernment?.id,
            lgaOfOrigin: personalInformation?.lgaOfOrigin?.id,
            nationalityId: personalInformation?.nationality?.id,
            stateId: personalInformation?.localGovernment?.state?.id,
            stateOfOrigin: personalInformation?.lgaOfOrigin?.state?.id,
            streetName: personalInformation?.streetName,
            proofOfAddressReference: personalInformation?.proofOfAddressReference,
            key: KYCFormKeys.PERSONAL_INFORMATION,
            personalAddressDocumentType: personalInformation?.personalAddressDocumentType,
        };
        return kycPersonalInformation;
    }

    private static getBusinessInformation(res) {
        const businessKycInformation: BusinessKycInformation = {
            businessMode: res?.businessMode,
            businessAddress: res?.businessAddress,
            city: res?.city,
            lgaId: res?.localGovernment?.id,
            localGovernment: res?.localGovernment,
            state: res?.state,
            businessSubCategory: res?.businessSubCategory,
            businessCategory: res?.businessCategory,
            isPersonalAddressForBusiness: res?.isPersonalAddressForBusiness,
            accountForOutsideBusinessCategory: res?.accountForOutsideBusinessCategory,
            otherBusinessPurpose: res?.otherBusinessPurpose,
            businessName: res?.businessName,
            businessType: res?.businessType,
            otherAccountPurpose: res?.otherAccountPurpose,
            businessAddressDocumentType: res?.businessAddressDocumentType,
            proofOfAddressReference: res?.proofOfAddressReference,
            businessDescription: res?.businessDescription,
            staffSize: res?.staffSize,
            ownershipStructure: res?.ownershipStructure,
            registeredBusinessType: res?.registeredBusinessType,
            businessOwnerGreaterThanOne: res?.businessOwnerGreaterThanOne,
        };
        return businessKycInformation;
    }

    callProofOfAddress(params) {
        this.proofAddressObserver.next(params);
    }

    savePendingBusinessKYCDetails(details) {
        if (!details) {
            this.localStorage.deleteValue('save_pending_business_kyc_details');
        }
        this.localStorage.save('save_pending_business_kyc_details', JSON.stringify(details));
    }

    getPendingBusinessKYCDetails() {
        const pendingBusinessKYCDetails = this.localStorage.get('save_pending_business_kyc_details');
        if (!pendingBusinessKYCDetails) {
            return null;
        }
        return JSON.parse(pendingBusinessKYCDetails);
    }

    callBusinessDetailsStatusObserver() {
        this.setBusinessDetailsStatus.next(true);
    }

    setMetaMapButtonAttribute(flowId, metadata) {
        const metaMapBtn = document.getElementById('mati_button');
        metaMapBtn?.setAttribute('flowId', flowId);
        metaMapBtn?.setAttribute('metadata', metadata);
    }

    setMetaMapKycWaitingDetails(params) {
        this.metaMapKycWaitingDetails.next(params);
    }

    upDateKycData(key) {
        const kycData = this.getKycRequestData();
        kycData[key] = true;
        this.setKYCRequestData(kycData);
    }

    setKYCRequestData(kycRequestData: KYCRequestData) {
        this.localStorage.save('KYC_REQUEST_DATA', JSON.stringify(kycRequestData));
    }

    setKYCCorrectFlow() {
        this.localStorage.save('CORRECT_KYC_FLOW', true);
    }

    setKycRejectionParameter(params) {
        if (!params) {
            this.localStorage.deleteValue('KycRejectionParameter');
        }
        this.localStorage.save('KycRejectionParameter', JSON.stringify(params));
    }

    getKycRejectionParameter() {
        const kycRejectionParameter = this.localStorage.get('KycRejectionParameter');
        if (!kycRejectionParameter) {
            return null;
        }
        return JSON.parse(kycRejectionParameter);
    }

    getSavedPendingKYCRequest() {
        const request = this.localStorage.get('Saved_Pending_KYC_Request');
        if (!request) {
            return null;
        }
        return JSON.parse(request);
    }

    setSavedPendingKYCRequest(request) {
        if (!request) {
            this.localStorage.deleteValue('Saved_Pending_KYC_Request');
            return;
        }
        this.localStorage.save('Saved_Pending_KYC_Request', JSON.stringify(request));
    }

    savedPendingKYCRequestError(isError = false) {
        this.localStorage.save('Saved_Pending_KYC_Request_Error', JSON.stringify(isError));
    }

    getPendingKYCRequestError() {
        const isError = this.localStorage.get('Saved_Pending_KYC_Request_Error');
        if (!isError) {
            return false;
        }
        return JSON.parse(isError);
    }

    setSelectedBusinessType(type) {
        this.selectedBusinessType.next(type);
        const kycData = this.getKycRequestData();
        const kycBusinessData = kycData.businessKycInformation;
        if (kycBusinessData) {
            kycBusinessData.businessType = type;
            kycData.businessKycInformation = kycBusinessData;
            this.setKYCRequestData(kycData);
        }
        this.localStorage.save('KYC_SELECTED_BUSINESS_TYPE', type);
    }

    getSelectedBusinessType() {
        return this.localStorage.get('KYC_SELECTED_BUSINESS_TYPE') || KycBusinessType.UNREGISTERED;
    }

    enforceKYCCorrectFlow() {
        let correctKycFlow = this.localStorage.get('CORRECT_KYC_FLOW');
        correctKycFlow = correctKycFlow ? JSON.parse(correctKycFlow) : false;
        if (!correctKycFlow) {
            this.router.navigateByUrl('/kyc');
            return;
        }
    }

    savedSelectedRegisteredBusinessType(registeredBusinessType: string) {
        this.localStorage.save('SELECTED_REGISTERED_BUSINESS_TYPE', registeredBusinessType?.toLocaleUpperCase());
    }

    getSelectedRegisteredBusinessType() {
        return this.localStorage.get('SELECTED_REGISTERED_BUSINESS_TYPE');
    }

    setPreviousRouteKey(key) {
        this.localStorage.save('previousKycRouteKey', key);
    }

    getPreviousKycRouteKey() {
        return this.localStorage.get('previousKycRouteKey');
    }

    resetSuccessfulKycSteps() {
        this.localStorage.save('successfulKycSteps', '');
    }

    setSuccessfulKycSteps(step, steps = []) {
        let successfulSteps = this.getSuccessfulKycSteps() || ',';
        if (steps.length) {
            let newSuccessSteps = ',';
            steps.forEach((e) => {
                newSuccessSteps = successfulSteps.includes(e) ? `${newSuccessSteps}` : `${e},${newSuccessSteps}`;
            });
            successfulSteps = `${newSuccessSteps},${successfulSteps}`;
            this.saveSuccessfulKycSteps(successfulSteps);
            return;
        }
        successfulSteps = successfulSteps.includes(step) ? successfulSteps : `,${step},${successfulSteps}`;
        this.saveSuccessfulKycSteps(successfulSteps);
    }

    saveSuccessfulKycSteps(steps) {
        // this.successfulKycSteps = steps;
        this.localStorage.save('successfulKycSteps', steps);
    }

    getSuccessfulKycSteps() {
        return this.localStorage.get('successfulKycSteps');
    }

    setCurrentKycRouteKey(key) {
        this.localStorage.save('currentKycRouteKey', key);
    }

    getCurrentKycRouteKey() {
        return this.localStorage.get('currentKycRouteKey');
    }

    setSkippedSteps(skipped) {
        this.localStorage.save('SkippedSteps', skipped);
    }

    getAddressQueryParam() {
        let queryParams;
        const skippedStep = this.getUpgradeSkippedSteps();
        const isProofOfAddress = skippedStep.includes(KYCFormKeys.PROOF_OF_ADDRESS);
        queryParams = isProofOfAddress ? { addressType: KYC_DOCUMENT_TYPE.personal } : null;
        const isBusinessAddress = skippedStep.includes(KYCFormKeys.BUSINESS_ADDRESS) || skippedStep.includes(KYCFormKeys.BUSINESS_PROOF_OF_ADDRESS);
        queryParams = queryParams === null && isBusinessAddress ? { addressType: KYC_DOCUMENT_TYPE.business } : queryParams;
        return queryParams;
    }

    routeUpgradeAccountSkippedStep(key = null, keys = []) {
        let skippedStep = this.getUpgradeSkippedSteps();
        skippedStep = key ? skippedStep.replace(`${key}`, '') : skippedStep;
        keys.forEach((e) => {
            skippedStep = skippedStep.replace(`${e}`, '');
        });
        this.setUpgradeSkippedSteps(skippedStep);
        const routeArray = skippedStep.split(',');
        const routeKey: string = routeArray.find((x) => {
            if (!StringHelper.isNullOrEmptyOrUndefined(x)) {
                return x;
            }
        });
        let route = KYCBusinessOwnerUpgradeInformation?.steps?.find((x) => {
            if (routeKey === x.key) {
                return x;
            }
        });
        if (!route) {
            route = KycUnregisteredBusinessUpgradeInformation?.steps.find((x) => `${x.key}` === `${routeKey}`);
        }
        this.setCurrentKycRouteKey(route?.key);
        const queryParams =
            route?.key === KYCFormKeys.PROOF_OF_ADDRESS || route?.key === KYCFormKeys.BUSINESS_PROOF_OF_ADDRESS ? this.getAddressQueryParam() : null;
        if (queryParams) {
            this.router.navigate([`${route?.route}`], { queryParams });
        } else {
            this.router.navigateByUrl(route?.route);
        }
        this.callNavigationObserver(route?.key);
    }

    lowLevelRoute() {
        const keys = [KYCFormKeys.LIVELINESS, KYCFormKeys.IDENTITY_VERIFICATION, KYCFormKeys.BUSINESS_TYPE];
        this.routeSkippedStep('', keys);
    }

    businessRoute() {
        const keys = [KYCFormKeys.LIVELINESS, KYCFormKeys.IDENTITY_VERIFICATION];
        this.routeSkippedStep('', keys);
    }

    routeSkippedStep(key = null, keys = []) {
        let skippedStep = this.getSkippedSteps();
        skippedStep = key ? skippedStep.replace(`${key}`, '') : skippedStep;
        keys.forEach((e) => {
            skippedStep = skippedStep.replace(`${e}`, '');
        });
        this.setSkippedSteps(skippedStep);
        const routeArray = skippedStep.split(',');
        const routeKey = routeArray.find((x) => {
            if (!StringHelper.isNullOrEmptyOrUndefined(x)) {
                return x;
            }
        });
        const isRegisteredBusiness = this.getSelectedBusinessType() === KycBusinessType.REGISTERED;
        let route = KYCBusinessOwnerInformation?.steps.find((x) => x.key === routeKey);
        if (!route) {
            route = isRegisteredBusiness
                ? kycRegisteredBusinessInformation?.steps.find((x) => x.key === routeKey)
                : kycUnregisteredBusinessInformation?.steps.find((x) => x.key === routeKey);
        }
        if (!route) {
            route = isRegisteredBusiness
                ? kycRegisteredBusinessInformation?.steps.find((x) => x.key === KYCFormKeys.SIGNATURE_UPLOAD)
                : kycUnregisteredBusinessInformation?.steps.find((x) => x.key === KYCFormKeys.SIGNATURE_UPLOAD);
        }
        this.setCurrentKycRouteKey(route?.key);
        this.router.navigateByUrl(route?.route);
        this.callNavigationObserver(route?.key);
        this.callBusinessInformationObserver(route?.key);
    }

    goBackToPreviousForm(key, link = null) {
        if (link) {
            this.router.navigateByUrl(link);
            return;
        }
        let skippedStep = this.getSkippedSteps();
        skippedStep = `,${key},${skippedStep}`;
        this.setSkippedSteps(skippedStep);
        this.routeSkippedStep();
    }

    getSkippedSteps() {
        return this.localStorage.get('SkippedSteps');
    }

    setUpgradeSkippedSteps(skipped) {
        return this.localStorage.save('upgrade_skippedSteps', skipped);
    }

    getUpgradeSkippedSteps() {
        return this.localStorage.get('upgrade_skippedSteps');
    }

    clearKYCData() {
        this.localStorage.deleteValue('SkippedSteps');
        this.localStorage.deleteValue('KycRejectionParameter');
        this.localStorage.deleteValue('CORRECT_KYC_FLOW');
        this.localStorage.deleteValue('current_kycProcess');
        this.localStorage.deleteValue('KYC_ROUTES');
        this.localStorage.deleteValue('currentKycRouteKey');
        // this.localStorage.deleteValue('SHOULD_COMPLETE_SELF_ONBOARDING_KYC');
        this.localStorage.deleteValue('KYC_REQUEST_DATA');
        this.localStorage.deleteValue('CURRENT_KYC_ROUTE');
        this.localStorage.deleteValue('Saved_Pending_KYC_Request');
        this.localStorage.deleteValue('successfulKycSteps');
        this.localStorage.deleteValue('kyc_business_information');
        this.localStorage.deleteValue('KYC_SELECTED_BUSINESS_TYPE');
        this.localStorage.deleteValue('kyc_business_owner_information');
        this.localStorage.deleteValue('save_pending_business_kyc_details');
    }

    clearMetaData() {
        this.localStorage.deleteValue('META_MAP_REF');
    }

    clearMetaReferenceData() {
        this.localStorage.deleteValue('META_MAP_REF_PI');
    }

    setSavedRegisteredBusinessDocument(data) {
        if (!data) {
            this.localStorage.deleteValue('REGISTER_BUSINESS_DOCUMENT');
            return;
        }
        this.localStorage.save('REGISTER_BUSINESS_DOCUMENT', JSON.stringify(data));
    }

    getSavedRegisteredBusinessDocument() {
        const savedData = this.localStorage.get('REGISTER_BUSINESS_DOCUMENT');
        if (!savedData) {
            return null;
        }
        return JSON.parse(savedData);
    }

    setKYCData(res) {
        if (!res) {
            this.setKYCRequestData({
                personalInformation: null,
            });
            return;
        }
        const personalInfo = res?.personalInformation;
        const nextOfKin = res?.nextOfKinInformation;
        const personalInformation: KycPersonalInformation = KycService.getPersonalInformation(personalInfo);
        const nextOfKinInformation: KycNextOfKin = KycService.getNextOfKin(nextOfKin);
        const businessProofOfAddress = new KycProofOfAddress();
        businessProofOfAddress.proofOfAddressReference = res?.businessKycInformation?.proofOfAddressReference;
        businessProofOfAddress.businessAddressDocumentType = res?.businessKycInformation?.businessAddressDocumentType;
        const data: KYCRequestData = {
            personalInformation,
            nextOfKinInformation,
            has_business_proof_of_address: res?.has_business_proof_of_address,
            has_completed_business_address: res?.has_completed_business_address,
            has_completed_business_documentation: res?.has_completed_business_documentation,
            has_completed_business_information: res?.has_completed_business_information,
            has_completed_identity_verification: res?.has_completed_identity_verification,
            has_completed_liveliness_verification: res?.has_completed_liveliness_verification,
            has_completed_next_of_kin_information: res?.has_completed_next_of_kin_information,
            has_completed_personal_information: res?.has_completed_personal_information,
            has_proof_of_address: res?.has_proof_of_address,
            has_uploaded_signature_file: res?.has_uploaded_signature_file,
            businessType: res?.businessType,
            businessKycInformation: KycService.getBusinessInformation(res?.businessKycInformation),
            identityVerificationCount: res?.identityVerificationCount,
            identityVerificationReference: res?.identityVerificationReference,
            identityVerificationSuspensionReleaseTime: res?.identityVerificationSuspensionReleaseTime,
            providerIdentityVerificationStatus: res?.providerIdentityVerificationStatus,
            bvnIdentityVerificationStatus: res?.bvnIdentityVerificationStatus,
            businessProofOfAddress,
            customer: res.customer,
            registered_business_fields: res?.registered_business_fields,
        };
        this.setKYCRequestData(data);
    }

    startBusinessCreationPhase(fatal = false) {
        const isMobile = window.innerWidth <= this.MAX_MOBILE_WIDTH;
        const defaultURL = isMobile ? '/kyc/setup-business' : KYCFormRoute.BUSINESS_TYPE;
        if (fatal) {
            this.router.navigateByUrl(`${defaultURL}`);
            return;
        }
        const successfulSteps = this.getSuccessfulKycSteps();
        const businessTypeSelectionSuccessful = successfulSteps.includes(KYCFormKeys.BUSINESS_TYPE);
        if (businessTypeSelectionSuccessful) {
            let skippedSteps = this.getSkippedSteps();
            skippedSteps = skippedSteps.includes(KYCFormPhase.BUSINESS_INFORMATION)
                ? skippedSteps
                : `,${KYCFormPhase.BUSINESS_INFORMATION},${skippedSteps}`;
            this.setSkippedSteps(skippedSteps);
            this.routeSkippedStep();
        } else {
            this.router.navigateByUrl(`${defaultURL}`);
        }
    }

    startBusinessCreationPhaseUpgrade(fatal = false) {
        const isMobile = window.innerWidth <= this.MAX_MOBILE_WIDTH;
        const defaultURL = isMobile ? '/kyc/setup-business' : KYCFormRoute.BUSINESS_TYPE;
        if (fatal) {
            this.router.navigateByUrl(`${defaultURL}`);
            return;
        }
        const successfulSteps = this.getSuccessfulKycSteps();
        const businessTypeSelectionSuccessful = successfulSteps.includes(KYCFormKeys.BUSINESS_TYPE);
        if (businessTypeSelectionSuccessful) {
            let skippedSteps = this.getUpgradeSkippedSteps();
            skippedSteps = skippedSteps.includes(KYCFormPhase.BUSINESS_INFORMATION)
                ? skippedSteps
                : `,${KYCFormPhase.BUSINESS_INFORMATION},${skippedSteps}`;
            this.setUpgradeSkippedSteps(skippedSteps);
            this.routeUpgradeAccountSkippedStep();
        } else {
            this.router.navigateByUrl(`${defaultURL}`);
        }
    }

    kycCompletion() {
        this.generalService.logout();
    }

    saveCurrentRoute(route) {
        if (!route) {
            return;
        }
        this.localStorage.save('CURRENT_KYC_ROUTE', JSON.stringify(route));
    }

    saveMetaMapReference(reference: string) {
        if (!reference || reference === 'null') {
            this.localStorage.deleteValue('META_MAP_REF');
        }
        this.localStorage.save('META_MAP_REF', reference);
    }

    getSavedMetaMapReference() {
        return this.localStorage.get('META_MAP_REF');
    }

    saveMetaMapPersonalInfoReference(reference: string) {
        if (!reference || reference === 'null') {
            this.localStorage.deleteValue('META_MAP_REF_PI');
        }
        this.localStorage.save('META_MAP_REF_PI', reference);
    }

    getSaveMetaMapPersonalInfoReference() {
        return this.localStorage.get('META_MAP_REF_PI');
    }

    resetKYCFlow() {
        this.router.navigateByUrl(KycService.BASE_KYC_URL);
    }

    callNavigationObserver(params) {
        this.kycNavigationObserver.next(params);
    }

    callBusinessInformationObserver(params) {
        this.kycBusinessInformationObserver.next(params);
    }

    setDirectorsFieldName(directorsFieldName) {
        this.localStorage.save('directorsFieldName', directorsFieldName);
    }

    getDirectorsFieldName() {
        return this.localStorage.get('directorsFieldName');
    }

    getKycRoutes(): KycRoute[] {
        const isRegisteredBusiness = this.getSelectedBusinessType() === KycBusinessType.REGISTERED;
        let defaultRoute = isRegisteredBusiness ? KycRegisteredBusinessRoutes : KycUnregisteredBusinessRoutes;
        if (!this.getShowKycRegisteredBusinessConfig()) {
            defaultRoute = KycUnregisteredBusinessRoutes;
        }
        let savedRoutes = this.localStorage.get('KYC_ROUTES');
        savedRoutes = savedRoutes ? JSON.parse(savedRoutes) : defaultRoute;
        return savedRoutes;
    }

    getCurrentRoute(): KycRoute {
        let currentRoute = this.localStorage.get('CURRENT_KYC_ROUTE');
        currentRoute = currentRoute ? JSON.parse(currentRoute) : null;
        return currentRoute;
    }

    getCompletedKyc() {
        let kyc = localStorage.getItem('completed-kyc');
        kyc = kyc ? JSON.parse(kyc) : false;
        return this.completedKyc || kyc;
    }

    getKYCProgress() {
        let progress = this.localStorage.get('KYC_PROGRESS_PERCENTAGE');
        progress = progress ? JSON.parse(progress) : 0;
        return progress;
    }

    setSelectedDirector(director: any) {
        if (!director) {
            this.localStorage.deleteValue('selected_director');
            return;
        }
        this.localStorage.save('selected_director', JSON.stringify(director));
    }

    getSelectedDirector() {
        const director = this.localStorage.get('selected_director');
        if (!director) {
            return null;
        }
        return JSON.parse(director);
    }

    setKycProgress(data) {
        const processKeys = KycProcessKeys;
        let totalCompleted = 0;
        processKeys.forEach((e) => {
            if (data[`${e}`]) {
                totalCompleted++;
            }
        });
        if (totalCompleted === 0) {
            this.localStorage.save('KYC_PROGRESS_PERCENTAGE', `${totalCompleted}`);
            return;
        }
        let percentageProgress = (totalCompleted / processKeys.length) * this.PERCENTAGE_MULTIPLIER;
        percentageProgress = Math.round(percentageProgress);
        this.localStorage.save('KYC_PROGRESS_PERCENTAGE', `${percentageProgress}`);
    }

    getKycRequestData(): KYCRequestData {
        let savedKycBusinessOwnerData = this.localStorage.get('KYC_REQUEST_DATA');
        savedKycBusinessOwnerData = savedKycBusinessOwnerData ? JSON.parse(savedKycBusinessOwnerData) : this.DEFAULT_BUSINESS_OWNER_KYC_DATA;
        return savedKycBusinessOwnerData;
    }

    getKycPreviewData(): Promise<KycPreviewData> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/agent-kyc-verification/kyc-preview-data`;
        return this.apiClientHelper.getForPromise(url);
    }

    getFlowId() {
        return this.config.META_MAP_FLOW_ID;
    }

    getLowLevelAccountFlowId() {
        return this.config.META_MAP_LOW_LEVEL_ACCOUNT_FLOW_ID;
    }

    getPersonalInfoFlowId() {
        return this.config.META_MAP_PERSONAL_INFO_FLOW_ID;
    }

    setStep(step: number) {
        this._step.next(step);
    }

    saveKycBVN(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/agent-kyc-verification/kyc-bvn-request`;
        return this.apiClientHelper.newPostAsFormDataForPromise(url, params);
    }

    saveKycDocument(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/agent-kyc-verification/kyc-document-request`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
    }

    saveKycNextOfKin(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/agent-kyc-verification/kyc-next-of-kin-request`;
        return this.apiClientHelper.putForPromiseI(url, params);
    }

    completeKycProcess(): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/agent-kyc-verification/kyc-complete-request`;
        return this.apiClientHelper.putForPromiseI(url, {});
    }

    updateIdDocument(id, body) {
        const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/${id}/kycs/identification`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, body);
    }

    updateProofOfAddress(id, body) {
        const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/${id}/kycs/address-proof`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, body);
    }

    uploadKYCSignature(body) {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/upload-signature`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, body);
    }

    updateAddress(id, body) {
        const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/${id}/kycs/address-information`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, body);
    }

    updateGeneralInfo(id, body) {
        const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/${id}/kycs/general-information`;
        return this.apiClientHelper.putForPromise(url, body);
    }

    updateSignature(id, body) {
        const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/${id}/kycs/signature`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, body);
    }

    changeRoute(data) {
        this.data.next(data);
    }

    getPendingKYCRequest() {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs`;
        return this.apiClientHelper.getForPromise(url);
    }

    setBusinessOwnerKycRejectionReason(reason) {
        this.localStorage.save('businessOwnerKycRejectionReason', reason);
    }

    getBusinessOwnerKycRejectionReason() {
        return this.localStorage.get('businessOwnerKycRejectionReason');
    }

    getKYCIdentityVerificationStatus() {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/identity-verification-status`;
        return this.apiClientHelper.getForPromise(url);
    }

    getShowKycRegisteredBusinessConfig() {
        return this.config.SHOW_KYC_REGISTERED_BUSINESS;
    }

    finalOnboarding(params) {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/self-onboarding/steps/complete-business-owner-onboarding`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    updatePersonalInformation(payload): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/personal-information`;
        return this.apiClientHelper.putForPromiseI(url, payload);
    }

    updateNextOfKin(payload): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/next-of-kin`;
        return this.apiClientHelper.putForPromiseI(url, payload);
    }

    updateBusinessOwnerProofOfAddress(addressType, formData): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/proof-of-address/${addressType}`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, formData);
    }

    uploadDocument(documentType, params, contentType): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/upload-file/${documentType}?contentType=${contentType}`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
    }

    uploadDocumentV2(params, addressType): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/address-information/${addressType}`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, params, true);
    }

    getMetaMapReference(params = 'identity'): Promise<MetaMapMetaData> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/identity/start?verificationType=${params}`;
        return this.apiClientHelper.postForPromise(url, {});
    }

    getMetaMapPersonalInfoReference(): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/metamap-verification/generate-reference`;
        return this.apiClientHelper.getForPromise(url);
    }

    getMetaMapPersonalInfoReferenceStatus(reference: string): Promise<MetaMapRefVerificationStatus> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/metamap-verification/${reference}/status/`;
        return this.apiClientHelper.getForPromise(url);
    }

    getMetaMapPersonalLivelinessReference(): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/staff-user/metamap/reference `;
        return this.apiClientHelper.getForPromise(url);
    }

    getMetaMapPersonalLivelinessReferenceStatus(reference: string): Observable<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/metamap-verification/${reference}/status/`;
        return this.apiClientHelper.getForObservable(url);
    }

    getMetamapVerification(reference): Observable<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/metamap-verification/${reference}/status/`;
        return this.apiClientHelper.getForObservable<any>(url).pipe(
            mergeMap((response) => {
                if (response?.verified) {
                    return of(response);
                } else {
                    return throwError(response);
                }
            }),
            retryWhen((errors) => {
                return errors.pipe(
                    delay(2000),
                    take(5),
                    catchError((error) => {
                        // Handle the error
                        return throwError('Error: Could not get data');
                    }),
                );
            }),
        );
    }

    getIdentityVerificationStatus(params): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/identity-verification-status?verificationType=${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getIdentityVerificationStatusV2(route): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `${route}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getBusinessTypes(): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/business-type`;
        return this.apiClientHelper.getForPromise(url);
    }

    getBusinessModes(): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/business-mode`;
        return this.apiClientHelper.getForPromise(url);
    }

    validateBusinessName(params): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/uniqueness-validation/business-name`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    updateBusinessDetails(params): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/business-details`;
        return this.apiClientHelper.putForPromise(url, params);
    }

    updateRegisteredBusinessDocuments(params): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/update-business-documents`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
    }

    getRegisteredKycRequestData(): KYCRegisteredRequestData {
        let savedKycBusinessOwnerData = this.localStorage.get('KYC_REQUEST_DATA_REGISTERED');
        savedKycBusinessOwnerData = savedKycBusinessOwnerData ? JSON.parse(savedKycBusinessOwnerData) : this.DEFAULT_BUSINESS_OWNER_KYC_DATA;
        // return this.KYCRegisteredRequestData || savedKycBusinessOwnerData;
        return savedKycBusinessOwnerData;
    }

    setKYCRegisteredRequestData(kycRequestData: KYCRegisteredRequestData) {
        // this.kycRequestData = kycRequestData;
        this.localStorage.save('KYC_REQUEST_DATA_REGISTERED', JSON.stringify(kycRequestData));
    }

    addDirector(value) {
        value.id = Math.floor(Math.random() * 10);
        let directors = JSON.parse(this.localStorage.get('directors'));
        if (directors && directors.length) {
            directors.push(value);
        } else {
            directors = [];
            directors = [value];
        }
        this.localStorage.save('directors', JSON.stringify(directors));
    }

    getDirectors() {
        const directors = this.localStorage.get('directors');
        if (directors && JSON.parse(directors).length) {
            return JSON.parse(directors);
        }
        return [];
    }

    businessAffiliate(params, type): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/business-affiliate/${type}`;
        return this.apiClientHelper.newPostAsFormDataForPromise(url, params);
    }

    updateBusinessAffiliate(params, id): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/business-affiliate/${id}`;
        return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
    }

    getBusinessAffiliate(): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/business-affiliate/all`;
        return this.apiClientHelper.getForPromise(url);
    }

    deleteDirector(id, directors) {
        const index = directors.map((value) => value.id).indexOf(id);
        if (index > -1) {
            directors.splice(index, 1);
        }
        this.localStorage.save('directors', JSON.stringify(directors));
    }

    getRegisteredBusinessTypes(): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/registered-business-types`;
        return this.apiClientHelper.getForPromise(url);
    }

    setBusinessTypeDisplayName(name) {
        this.localStorage.save('Business_Type_Display_Name', name);
    }

    getBusinessTypeDisplayName() {
        return this.localStorage.get('Business_Type_Display_Name');
    }

    deleteAffiliate(id): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/business-kycs/business-affiliate/${id}`;
        return this.apiClientHelper.delete(url);
    }

    getRouteWithRoutePath(routePath: RoutePath, kycFormPhase: KYCFormPhase) {
        return this.getKycRoutes().find((x) => x.route?.includes(kycFormPhase) && !x.completed && x.route?.includes(routePath));
    }

    getRoutePath(kycFormPhase: KYCFormPhase) {
        return this.getKycRoutes().find((x) => x.route?.includes(kycFormPhase) && !x.completed);
    }

    setBusinessTransactionPin(params): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents/pin`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    setMetaMapPerformed(param) {
        this.localStorage.save('meta_map_performed', param);
    }

    getMetaMapPerformed() {
        const metaMapPerformed = this.localStorage.get('meta_map_performed');
        return metaMapPerformed ? metaMapPerformed : null;
    }

    setKycBusinessOwnerInformation(kycBusinessOwnerInformation: PrimerCard) {
        // this.kycBusinessOwnerInformation = kycBusinessOwnerInformation;
        this.localStorage.save('kyc_business_owner_information', JSON.stringify(kycBusinessOwnerInformation));
    }

    setKycBusinessInformation(kycBusinessInformation: PrimerCard) {
        // this.kycBusinessInformation = kycBusinessInformation;
        this.localStorage.save('kyc_business_information', JSON.stringify(kycBusinessInformation));
    }

    getLocalStorageSize() {
        let total = 0;
        // tslint:disable-next-line:forin
        for (const x in localStorage) {
            // Value is multiplied by 2 due to data being stored in `utf-16` format, which requires twice the space.
            const amount = (localStorage[x].length * 2) / 1024 / 1024;
            if (!isNaN(amount) && localStorage.hasOwnProperty(x)) {
                total += amount;
            }
        }
        return total.toFixed(2);
    }

    getKycBusinessInformation() {
        // if (this.kycBusinessInformation) {
        // 	return this.kycBusinessInformation;
        // }
        const savedKycBusinessInformation = this.localStorage.get('kyc_business_information');
        if (!savedKycBusinessInformation) {
            return null;
        }
        return JSON.parse(savedKycBusinessInformation);
    }

    getKycBusinessOwnerInformation(): PrimerCard {
        // if (this.kycBusinessOwnerInformation) {
        // 	return this.kycBusinessOwnerInformation;
        // }
        const savedKycBusinessOwnerInformation = this.localStorage.get('kyc_business_owner_information');
        if (!savedKycBusinessOwnerInformation) {
            return null;
        }
        return JSON.parse(savedKycBusinessOwnerInformation);
    }

    isUnfilledForm(status, showStep) {
        return !status && showStep;
    }

    navigateKYCBusinessForm(form: PrimerCard) {
        let isBusinessInformation = form?.title === 'Business Information';
        isBusinessInformation ? this.setKycBusinessInformation(form) : this.setKycBusinessOwnerInformation(form);
        let unfilledForm = form?.steps.find((k) => k.status === false && k.showStep === true);
        if (StringHelper.isNullOrEmptyOrUndefined(unfilledForm)) {
            if (form?.title !== 'Business Information') {
                form = this.getKycBusinessInformation();
            } else {
                form = this.getKycBusinessOwnerInformation();
            }
            // form = !isBusinessInformation ? this.getKycBusinessInformation() : this.getKycBusinessOwnerInformation();
            unfilledForm = form?.steps.find((y) => y.status === false && y.showStep === true);
        }
        isBusinessInformation = form?.title === 'Business Information';
        const formKey = unfilledForm?.key || KYCFormKeys.SIGNATURE_UPLOAD;
        const route = unfilledForm?.route || KYCFormRoute.SUMMARY;
        this.updateCurrentKYCBusinessRoute(form, formKey);
        isBusinessInformation ? this.setKycBusinessInformation(form) : this.setKycBusinessOwnerInformation(form);
        this.router.navigateByUrl(route);
        this.callNavigationObserver(formKey);
        if (isBusinessInformation) {
            this.callBusinessInformationObserver(formKey);
        }
    }

    navigateKYCBusinessFormII(form: PrimerCard, key) {
        const unfilledForm = form?.steps.find((x) => x.key === key);
        const isBusinessInformation = form?.title === 'Business Information';
        this.updateCurrentKYCBusinessRoute(form, key);
        isBusinessInformation ? this.setKycBusinessInformation(form) : this.setKycBusinessOwnerInformation(form);
        this.router.navigateByUrl(unfilledForm?.route);
        this.callNavigationObserver(key);
        if (isBusinessInformation) {
            this.callBusinessInformationObserver(key);
        }
    }

    updateKYCBusinessForm(key, title = '') {
        const isBusinessInformation = title === 'Business Information';
        const currentKycForm = isBusinessInformation ? this.getKycBusinessInformation() : this.getKycBusinessOwnerInformation();
        currentKycForm?.steps.map((x) => (x.key === key ? (x.status = true) : x.status));
        isBusinessInformation ? this.setKycBusinessInformation(currentKycForm) : this.setKycBusinessOwnerInformation(currentKycForm);
        this.navigateKYCBusinessForm(currentKycForm);
    }

    updateCurrentKYCBusinessRoute(form: PrimerCard, key) {
        form?.steps.map((x) => (x.key === key ? (x.currentRoute = true) : (x.currentRoute = false)));
    }

    setCurrentKYCProcess(kycProcess: KycProcess) {
        this.localStorage.save('current_kycProcess', kycProcess);
    }

    getCurrentKYCProcess() {
        return this.localStorage.get('current_kycProcess');
    }

    protected processNavigation(currentRoute: KycRoute) {
        this.saveCurrentRoute(currentRoute);
        this.router.navigateByUrl(`${currentRoute.route}`);
        this.callNavigationObserver(currentRoute);
    }

    protected navigationByFormPhase(kycFormPhase: KYCFormPhase) {
        const route = this.getRoutePath(kycFormPhase);
        this.saveCurrentRoute(route);
        this.router.navigateByUrl(`${route.route}`);
        this.callBusinessInformationObserver(route);
    }
}

export enum KycProcess {
    ACCOUNT_UPGRADE = 'ACCOUNT_UPGRADE',
    FILE_RE_UPLOAD = 'FILE_RE_UPLOAD',
    REGULAR_KYC = 'FILE_RE_UPLOAD',
}
